import React from 'react';
import { EyeOff, Eye, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Hidden Tools Settings Panel
export const HiddenToolsSettingsPanel = ({
  hiddenTools,
  onRestoreTool,
  onRestoreAll,
  onClose
}) => {
  return (
    <div className="mb-6 bg-gray-50 border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Dashboard Tools Settings</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {hiddenTools.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              {hiddenTools.length} tool{hiddenTools.length > 1 ? 's' : ''} currently hidden
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onRestoreAll}
              className="text-sm"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Restore All
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {hiddenTools.map(tool => {
              const Icon = tool.icon;
              return (
                <div
                  key={tool.key}
                  className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${tool.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{tool.name}</span>
                  </div>
                  <button
                    onClick={() => onRestoreTool(tool.settingsKey)}
                    className="p-1.5 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                    title="Restore to dashboard"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">
          No tools are currently hidden. Hover over any tool and click the eye icon to hide it.
        </p>
      )}
    </div>
  );
};

// Hidden Tools Banner
export const HiddenToolsBanner = ({
  hiddenTools,
  showHiddenTools,
  setShowHiddenTools,
  onRestoreTool,
  onRestoreAll
}) => {
  if (hiddenTools.length === 0) return null;

  return (
    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <EyeOff className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-900">
              {hiddenTools.length} tool{hiddenTools.length > 1 ? 's' : ''} hidden
            </p>
            <p className="text-sm text-amber-700">
              Click to view and restore hidden tools
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHiddenTools(!showHiddenTools)}
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            <Eye className="w-4 h-4 mr-2" />
            {showHiddenTools ? 'Hide List' : 'View Hidden'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRestoreAll}
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restore All
          </Button>
        </div>
      </div>

      {/* Hidden Tools List */}
      {showHiddenTools && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {hiddenTools.map(tool => {
              const Icon = tool.icon;
              return (
                <div
                  key={tool.key}
                  className="flex items-center justify-between bg-white rounded-lg border border-amber-200 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-gradient-to-br ${tool.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{tool.name}</span>
                  </div>
                  <button
                    onClick={() => onRestoreTool(tool.settingsKey)}
                    className="p-1.5 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                    title="Restore to dashboard"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
